// The session: the page's reads, fetched once and kept.
//
// Home used to open with fifteen or sixteen requests to nine endpoints — the
// same endpoint from several components — and every navigation started over,
// because nothing held a response beyond the component that asked for it.
// GET /api/session answers the seven reads the shell and Home need in one
// round trip (routes/session.js composes the endpoints themselves, so each
// part is exactly what the endpoint returns). This module primes a cache
// from those parts and lets the existing wrappers (fetchSkillVerdicts,
// fetchCards, …) read from it through `sessionFetch`, which hands back a real
// Response so no wrapper's parsing or error handling changes.
//
// Freshness is explicit, not guessed: a wrapper that writes calls
// `invalidate(...)` for the parts it changed; the change events the app
// already raises (kalibrSkillsChanged) invalidate the parts they imply; a
// part older than TTL_MS is refetched; and coming back to the tab after a
// minute away refetches everything. Every one of those refetches is answered
// from the server's memory (lib/graph-mirror.js), so being generous costs
// nothing — being stale would.

import { keepUsername, ownUsername } from './accountBase';
import { CAPTURE_ENABLED } from './features';
import { keptFirstLook } from './firstLook';
import { currentUserId } from './storage';

/** Raised after every successful write the app makes (installSessionWriteWatch); once for writes held together
 *  (holdWrites). */
export const WROTE_EVENT = 'kalibrWrote';

export type SessionKey = 'config' | 'profile' | 'contexts' | 'onboarding' | 'verdicts' | 'cards' | 'jobSpine' | 'grant' | 'inbox' | 'world' | 'atlas';

/** `etag`: the tag the part's route named it by, handed on with it (lib/api/world: a copy kept by it is asked only
 *  whether it has moved). */
interface Entry { at: number; status: number; body: unknown; etag?: string | null }

const TTL_MS = 5 * 60_000;
const FOCUS_REFRESH_MS = 60_000;
// How long a read waits for a bootstrap in flight before reading on its own —
// a bootstrap that is slow or refused must not hold a page hostage.
const BOOTSTRAP_WAIT_MS = 2_500;

const entries = new Map<string, Entry>();
// `world`: the server's world (routes/world.js), in place of the verdict feed when the server draws it (lib/server-world.js)
const SESSION_KEYS: SessionKey[] = ['config', 'profile', 'contexts', 'onboarding', 'verdicts', 'cards', 'jobSpine', 'grant', 'inbox', 'world', 'atlas'];
let generation = 0;
const partGenerations = new Map<SessionKey, number>();
let viewGeneration = 0;
const partViewGenerations = new Map<SessionKey, number>();
const directReads = new Map<string, number>();
interface Bootstrap {
  promise: Promise<void>;
  generations: Map<SessionKey, string>;
  reads: Map<string, number>;
}
const bootstraps = new Map<string, Bootstrap>();
interface Flight {
  promise: Promise<Response>;
  controller: AbortController;
  readers: number;
  settled: boolean;
}
const flights = new Map<string, Flight>();
let lastActivityAt = Date.now();
let listenersInstalled = false;

const keyOf = (key: SessionKey, userId: string) => `${key}:${userId}`;

/** Pending readers and parsed-response wrappers share the same write boundary. */
export function sessionGeneration(key: SessionKey): string {
  return `${generation}:${partGenerations.get(key) ?? 0}`;
}

/** Loaded views survive routine revalidation, but never a write or sign-out. */
export function sessionViewGeneration(key: SessionKey): string {
  return `${viewGeneration}:${partViewGenerations.get(key) ?? 0}`;
}

function remember(key: SessionKey, userId: string, status: number, body: unknown, etag: string | null = null): void {
  entries.set(keyOf(key, userId), { at: Date.now(), status, body, etag });
}

function fresh(key: SessionKey, userId: string): Entry | null {
  const e = entries.get(keyOf(key, userId));
  if (!e) return null;
  if (Date.now() - e.at > TTL_MS) { entries.delete(keyOf(key, userId)); return null; }
  return e;
}

/** Synchronous first paint for an already confirmed, still-fresh read. A
 * stale entry, refusal, invalidation or sign-out never grants cached access. */
export function freshSessionValue<T>(key: SessionKey, userId: string): T | undefined {
  const entry = fresh(key, userId);
  return entry && entry.status >= 200 && entry.status < 300 ? entry.body as T : undefined;
}

/** Forget parts (or everything) so the next read goes to the server. */
export function invalidate(...keys: (SessionKey | 'all')[]): void {
  expire(keys, true);
}

/** Ask the server again while retaining the last successful rendered view. */
export function revalidate(...keys: (SessionKey | 'all')[]): void {
  expire(keys, false);
}

function expire(keys: (SessionKey | 'all')[], discardViews: boolean): void {
  if (keys.length === 0 || keys.includes('all')) {
    generation++;
    if (discardViews) viewGeneration++;
    entries.clear();
    return;
  }
  for (const key of keys) if (key !== 'all') {
    partGenerations.set(key, (partGenerations.get(key) ?? 0) + 1);
    if (discardViews) partViewGenerations.set(key, (partViewGenerations.get(key) ?? 0) + 1);
  }
  for (const k of entries.keys()) {
    for (const key of keys) if (k.startsWith(`${key}:`)) entries.delete(k);
  }
}

/** A 401 that means "no session for this browser" (lib/session-gate.js) —
 *  as opposed to a 401 a route passed through from Paradigm. */
async function sessionRequired(r: Response): Promise<boolean> {
  if (r.status !== 401) return false;
  try {
    const b = await r.clone().json() as { code?: string } | null;
    return !!b && b.code === 'SESSION_REQUIRED';
  } catch { return false; }
}

/** Someone is signed in to this page (lib/storage.ts STORAGE_KEYS.paradigmUserId). */
function signedIn(): boolean {
  return !!currentUserId();
}

/** The request names a person, in its query or a JSON body: the only kind the
 *  session gate refuses (lib/session-gate.js). A route that answers
 *  SESSION_REQUIRED for a request naming nobody (the public vouch form's) is
 *  asking its own visitor to sign in. */
function namesUser(url: string, init?: RequestInit): boolean {
  return /[?&]user_id=/.test(url) || (typeof init?.body === 'string' && init.body.includes('"user_id"'));
}

/** The OAuth return (pages/Callback.tsx) words its own refusals: its raw
 *  reads' 401 means "no session" there, never the app-wide sign-out. */
function onSignInReturn(): boolean {
  return /^\/callback(\.html)?$/.test(window.location?.pathname ?? '');
}

/** The server no longer holds a session for this browser (the cookie expired,
 *  or the sign-in was ended elsewhere): tell the shell ONCE, which signs the
 *  page out and asks for Paradigm again. */
let lostAnnounced = false;
function sessionLost(): void {
  if (lostAnnounced || typeof window === 'undefined') return;
  lostAnnounced = true;
  // Clear private snapshots before any listener navigates or starts a read.
  invalidate('all');
  window.dispatchEvent(new CustomEvent('kalibrSessionLost'));
}

/** A 403 that means "Paradigm has withdrawn Caliber's approval" — the one
 *  typed refusal the server's request boundary answers for a revoked person
 *  (server.js; lib/grant-health.js). Any other 403 is a route's own. */
async function grantRevoked(r: Response): Promise<{ reason: string | null } | null> {
  if (r.status !== 403) return null;
  try {
    const b = await r.clone().json() as { code?: string; reason?: string } | null;
    if (!b || b.code !== 'GRANT_REVOKED') return null;
    return { reason: typeof b.reason === 'string' ? b.reason : null };
  } catch { return null; }
}

/** WHETHER THIS DOCUMENT HAS WORKED. The frame's weight (components/common/
 *  GrantNotice.tsx) turns on one question: did the page beneath ever get a
 *  read through the server's request boundary (server.js)? That boundary
 *  refuses a revoked person on every /api path that names a user, except the
 *  ways out — sign-in, /api/config, /api/me/grant. So a 2xx on any other such
 *  path is proof the person was let through, and the standing's own read is
 *  never proof: the server's memo of it can lag a refusal by minutes, and a
 *  check that could not be made says nothing about the page. Kept for the
 *  life of the document; a later loss over a page that has worked leaves the
 *  page mounted, a loss over one that never has replaces it. */
let pageWorked = false;
const STANDING_EXEMPT_EXACT = ['/api/config', '/api/me/grant', '/api/llm/complete', '/api/automations/run', '/api/paradigm/webhook'];
const STANDING_EXEMPT_PREFIX = ['/api/auth/', '/api/admin/'];

/** A request the boundary would have refused for a revoked person: an /api
 *  path outside the ways out, naming a user in the query or a JSON body. */
function provesStanding(path: string, url: string, init?: RequestInit): boolean {
  if (!path.startsWith('/api/')) return false;
  if (STANDING_EXEMPT_EXACT.includes(path) || STANDING_EXEMPT_PREFIX.some((p) => path.startsWith(p))) return false;
  return namesUser(url, init);
}

/** The approval is gone: tell the shell ONCE per page, and remember when, so
 *  a frame that mounts after the fact can tell a loss that predates the page
 *  (nothing beneath could load) from one that arrived while it was working —
 *  the event carries `pageWorked` so the shell need not guess it from a
 *  standing that may be stale or unknown. Nothing is signed out and no
 *  storage is touched — the person's local drafts and the page they are on
 *  stay exactly where they are; the frame above them (components/common/
 *  GrantNotice.tsx) offers the way back. */
export interface GrantLost { at: number; reason: string | null; pageWorked: boolean }
let grantLostState: GrantLost | null = null;
function grantLost(reason: string | null): void {
  if (grantLostState || typeof window === 'undefined') return;
  grantLostState = { at: Date.now(), reason, pageWorked };
  // The bootstrap may have primed 'grant' as ok moments before the refusal:
  // the next standing read must go to the server.
  invalidate('grant');
  window.dispatchEvent(new CustomEvent('kalibrGrantLost', { detail: { state: 'revoked', reason, pageWorked } }));
}

/** The loss this page has seen, if any. */
export function grantLostSince(): GrantLost | null { return grantLostState; }

/** Re-arm once the standing is known to be good again (the frame's press
 *  found it already put right), so a later loss is announced afresh. */
export function clearGrantLost(): void { grantLostState = null; }

/** Resolves once this person's session read in flight, if any, has landed; never starts one. How a page draws its
 *  world is decided by what the session brought (lib/api/world drawsFromServer), without a read of its own. */
export function sessionSettled(userId: string): Promise<void> {
  const pending = bootstraps.get(userId);
  return pending ? pending.promise.then(() => undefined, () => undefined) : Promise.resolve();
}

/** Load the session once for this user. Safe to call repeatedly: one in flight. */
export function loadSession(userId: string): Promise<void> {
  if (!userId) return Promise.resolve();
  const pending = bootstraps.get(userId);
  if (pending) return pending.promise;
  installListeners();
  const slot: Bootstrap = {
    promise: Promise.resolve(),
    generations: new Map(SESSION_KEYS.map(key => [key, sessionGeneration(key)])),
    reads: new Map(directReads),
  };
  bootstraps.set(userId, slot);
  slot.promise = (async () => {
    try {
      // `capture=1`: the inbox part's checklist carries Capture's four people
      // only where Capture is switched on (routes/inbox.js).
      // `views=server`: this page can draw from the server's world, so the session may carry it in place of the
      // verdict feed (routes/session.js WORLD_PARTS); a server that does not draw it for this person sends the feed
      // `have=atlas:<tag>`: this device kept Home's first look (lib/firstLook) by the tag the server named it; the
      // atlas is asked only whether it has moved, and one that has not stands as kept
      const look = keptFirstLook(userId);
      const have = look?.tag ? `&have=${encodeURIComponent(`atlas:${look.tag}`)}` : '';
      const r = await fetch(`/api/session?user_id=${encodeURIComponent(userId)}&views=server${CAPTURE_ENABLED ? '&capture=1' : ''}${have}`);
      if (await sessionRequired(r)) { sessionLost(); return; }
      const revoked = await grantRevoked(r);
      if (revoked) { grantLost(revoked.reason); return; }
      if (!r.ok) return; // refused (no mirror, not authorised): the wrappers read individually
      pageWorked = true; // the boundary let this person through
      const data = await r.json() as { username?: string | null; parts?: Record<string, { status: number; body: unknown; etag?: string }> };
      // the username rides the session (routes/session.js): a rename on ofSelf
      // reaches the app with no read of its own (App follows USERNAME_EVENT)
      if (data?.username !== undefined && data.username !== ownUsername()) keepUsername(data.username);
      const parts = data && data.parts ? data.parts : {};
      for (const key of SESSION_KEYS) {
        // not moved since the copy this device kept (a 304, no body): that copy, as read
        const sent = parts[key];
        const part = key === 'atlas' && sent?.status === 304 && look?.tag && sent.etag === look.tag ? { status: 200, body: look.atlas, etag: look.tag } : sent;
        // A write supersedes this snapshot. So does a direct read started
        // after the bootstrap (including the timeout fallback still in flight).
        const cacheKey = keyOf(key, userId);
        if (part?.status === 200 && slot.generations.get(key) === sessionGeneration(key)
          && (slot.reads.get(cacheKey) ?? 0) === (directReads.get(cacheKey) ?? 0)) {
          remember(key, userId, part.status, part.body, part.etag ?? null);
        }
      }
    } catch {
      /* a failed bootstrap costs nothing: every wrapper falls back to its own read */
    } finally {
      if (bootstraps.get(userId) === slot) bootstraps.delete(userId);
    }
  })();
  return slot.promise;
}

function aborted(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('The operation was aborted.', 'AbortError');
}

function awaitBootstrap(key: SessionKey, userId: string, epoch: string, signal: AbortSignal): Promise<void> {
  const pending = bootstraps.get(userId);
  // A post-write reader must not wait for a bootstrap that predates its write.
  if (!pending || pending.generations.get(key) !== epoch) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const finish = (error?: unknown) => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      if (error !== undefined) reject(error); else resolve();
    };
    const onAbort = () => finish(aborted(signal));
    const timer = setTimeout(() => finish(), BOOTSTRAP_WAIT_MS);
    signal.addEventListener('abort', onAbort, { once: true });
    if (signal.aborted) onAbort();
    pending.promise.then(() => finish(), finish);
  });
}

function synthesise(status: number, body: unknown, etag?: string | null): Response {
  const text = typeof body === 'string' ? body : JSON.stringify(body ?? null);
  return new Response(text, { status, headers: { 'Content-Type': typeof body === 'string' ? 'text/plain' : 'application/json', ...(etag ? { ETag: etag } : {}) } });
}

/**
 * A fetch that reads from the session when it can. Returns a real Response
 * either way, so callers parse and report errors exactly as before. Only 2xx
 * answers are remembered; anything else is the network's to explain.
 */
async function observeResponse(r: Response, userId: string, url: string, init?: RequestInit): Promise<void> {
  if (userId && await sessionRequired(r)) sessionLost();
  if (userId) { const revoked = await grantRevoked(r); if (revoked) grantLost(revoked.reason); }
  if (userId && r.ok && provesStanding(url.split('?')[0], url, init)) pageWorked = true;
}

class SupersededRead extends Error {}

/** Every reader gets its own body and cancellation. The network belongs to
 * all readers; only the last reader leaving aborts it. */
function consume(flight: Flight, signal?: AbortSignal | null): Promise<Response> {
  flight.readers++;
  return new Promise((resolve, reject) => {
    let done = false;
    const release = () => {
      if (done) return false;
      done = true;
      signal?.removeEventListener('abort', onAbort);
      flight.readers--;
      if (!flight.readers && !flight.settled) flight.controller.abort();
      return true;
    };
    const onAbort = () => { if (release()) reject(aborted(signal!)); };
    signal?.addEventListener('abort', onAbort, { once: true });
    if (signal?.aborted) onAbort();
    flight.promise.then(response => {
      if (release()) resolve(response.clone());
    }, error => { if (release()) reject(error); });
  });
}

export async function sessionFetch(key: SessionKey, userId: string, url: string, init?: RequestInit): Promise<Response> {
  if (init?.signal?.aborted) throw aborted(init.signal);
  // The session is a read cache. Never coalesce mutations or their bodies.
  if ((init?.method ?? 'GET').toUpperCase() !== 'GET') {
    const response = await fetch(url, init);
    await observeResponse(response, userId, url, init);
    return response;
  }
  const { signal, headers, ...options } = init ?? {};
  const requestKey = JSON.stringify([key, userId, url, options, [...new Headers(headers)].sort()]);
  for (;;) {
    if (signal?.aborted) throw aborted(signal);
    const epoch = sessionGeneration(key);
    const hit = userId ? fresh(key, userId) : null;
    if (hit) return synthesise(hit.status, hit.body, hit.etag);
    const flightKey = `${epoch}:${requestKey}`;
    let flight = flights.get(flightKey);
    if (!flight || flight.controller.signal.aborted) {
      const slot: Flight = { promise: Promise.resolve(null as unknown as Response), controller: new AbortController(), readers: 0, settled: false };
      flight = slot;
      slot.promise = (async () => {
        if (userId) await awaitBootstrap(key, userId, epoch, slot.controller.signal);
        if (slot.controller.signal.aborted) throw aborted(slot.controller.signal);
        if (sessionGeneration(key) !== epoch) throw new SupersededRead();
        const primed = userId ? fresh(key, userId) : null;
        if (primed) return synthesise(primed.status, primed.body, primed.etag);
        const cacheKey = keyOf(key, userId);
        const read = (directReads.get(cacheKey) ?? 0) + 1;
        directReads.set(cacheKey, read);
        const response = await fetch(url, { ...init, signal: slot.controller.signal });
        await observeResponse(response, userId, url, init);
        if (userId && response.ok) {
          try {
            const body = await response.clone().json();
            if (!slot.controller.signal.aborted && sessionGeneration(key) === epoch && directReads.get(cacheKey) === read) {
              remember(key, userId, response.status, body, response.headers.get('ETag'));
            }
          } catch { /* not JSON — nothing to keep */ }
        }
        return response;
      })().finally(() => {
        slot.settled = true;
        if (flights.get(flightKey) === slot) flights.delete(flightKey);
      });
      flights.set(flightKey, slot);
    }
    try {
      const response = await consume(flight, signal);
      // A pre-write caller also advances to the current answer rather than
      // delivering a stale value into a still-mounted consumer.
      if (sessionGeneration(key) !== epoch && response.ok) continue;
      return response;
    } catch (error) {
      if (error instanceof SupersededRead) continue;
      throw error;
    }
  }
}

/** The change events the app already raises say which parts moved. */
function installListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  window.addEventListener('kalibrSkillsChanged', () => invalidate('verdicts', 'onboarding', 'cards', 'world', 'atlas'));
  window.addEventListener('kalibrHiddenSkillsChanged', () => invalidate('verdicts'));
  // The references desk announces its own writes (lib/api/referenceVerify.ts:
  // an ask answered, a counter accepted, a request sent) and every one moves
  // the tray's rows (lib/useInbox.ts re-reads on the same events).
  window.addEventListener('kalibrReferenceRequestsChanged', () => invalidate('inbox'));
  window.addEventListener('kalibrReferenceAsksChanged', () => invalidate('inbox'));
  document.addEventListener('visibilitychange', () => {
    // Returning to the browser tab makes reads stale, not the already drawn
    // page. Keep its last successful view while fresh answers arrive.
    if (document.visibilityState === 'visible' && Date.now() - lastActivityAt > FOCUS_REFRESH_MS) revalidate('all');
    lastActivityAt = Date.now();
  });
  window.addEventListener('focus', () => { lastActivityAt = Date.now(); });
}

/** Which parts a write to a path can have changed. Generous on purpose: a
 *  refetch is answered from the server's memory, a stale part is a bug. */
/** What a write can change: parts of the session, and `world`, the record the card world draws (components/world,
 *  lib/useWorld), read again when a write says so. `all` is every part, the world with them. */
type WritePart = SessionKey | 'all';

/** What a piece's write names in its JSON body (lib/api/recordEntries): its kind ('' where it names none), whether it
 *  draws lines as it is made, and whether one of them is to a card's pick, made yours first (docs/PIECE_SAVE.md). */
function entryOf(body: unknown): { kind: string; lines: boolean; claims: boolean } {
  const none = { kind: '', lines: false, claims: false };
  if (typeof body !== 'string') return none;
  try {
    const asked = JSON.parse(body) as { kind?: unknown; lines?: unknown } | null;
    const lines = Array.isArray(asked?.lines) ? asked.lines as { claim?: unknown }[] : [];
    return { kind: typeof asked?.kind === 'string' ? asked.kind : '', lines: lines.length > 0, claims: lines.some((l) => Boolean(l?.claim)) };
  } catch { return none; }
}

/** WHAT EACH WRITE CHANGES, ONE PLACE: a write that says nothing moves nothing. A page's own words (the body of a
 *  project, a role or a course, saved as it is typed) are no part of the session and nothing the world draws, so its
 *  saves move neither (a world read again on every pause in typing made the editor stutter, owner call 2026-10-15).
 *  `body`: the write's own, where what it moved turns on what it carries (a piece's kind). */
function partsTouchedBy(path: string, method = 'POST', body?: unknown): WritePart[] {
  if (path === '/api/me/project-page' || path === '/api/me/record-page') return [];
  // A PIECE OF WORK MADE OR CHANGED (routes/identity/record-entries.js). Until 2026-11-01 both fell to 'all', and a
  // project made read the wallet, the verdicts and the tray again, none of which holds it. The world draws it (and its
  // atlas). Made, it stands under no line yet, so no verdict and no checklist count (lines, lib/checklist-facts) moves;
  // changed, its name and dates stand in each verdict a line of it is in (routes/cards.js proofBySkill). A role, or a
  // piece whose kind is not told, is a job besides: the onboarding status, the job spine and the tray's next moves read
  // jobs (routes/directions.js tracks). No card and no account count stands on a piece. A removal and a copy: 'all'.
  // MADE WITH ITS LINES (docs/PIECE_SAVE.md, 2026-11-01): one request draws them and makes a card's picks yours, so it
  // moves what a line moves and, with a pick, what a claim moves: their own rules, below
  if (path === '/api/me/record-entry' && (method === 'POST' || method === 'PATCH')) {
    const asked = entryOf(body);
    const parts: WritePart[] = method === 'PATCH' ? ['world', 'verdicts'] : ['world'];
    const piece: WritePart[] = ['project', 'qualification'].includes(asked.kind) ? parts : [...parts, 'onboarding', 'jobSpine', 'inbox'];
    if (method === 'PATCH' || !asked.lines) return piece;
    return [...new Set([...piece, ...partsTouchedBy('/api/me/proof-links'), ...(asked.claims ? partsTouchedBy('/api/me/skills') : [])])];
  }
  // A LINE OF PROOF drawn, described or taken off (lib/proof-links), until 2026-11-01 'all': the world draws it; the
  // verdicts list the pieces each skill's lines run from and the files backing it; the tray's checklist counts the
  // skills proof stands under, and a file put on work is one of its documents. No card, no account count, no job moves.
  if (path === '/api/me/proof-links') return ['world', 'verdicts', 'inbox'];
  // what holds its own answer (a colour, a tag, a record's address, its own properties, a CV's tool dismissed:
  // lib/foundTools) and a word to the team move
  // nothing either: their stores keep what the write answered, and a whole world read again for one was a stall.
  // Nor does the visit's nudge to finish kept deletions (lib/pieceDeletions finishPieceDeletions): the server finishes
  // them later, and falling to 'all' it forgot every read the opening page was still making (the References desk was
  // left reading)
  if (['/api/me/badges', '/api/me/skill-tags', '/api/me/slugs', '/api/me/record-properties', '/api/me/ask-team', '/api/me/skill-tools/found/dismiss',
    '/api/me/piece-deletions/finish'].includes(path)) return [];
  // An error report (lib/telemetry) and an invite's preview (routes/cards.js skills-card-email-preview) write nothing
  // (2026-10-31): the report fell to 'all' and the preview to '/api/me/skills', so each one read the page again
  // A TOOL ON A SKILL (lib/skillTools): its own store keeps the answer, and no session part holds tools (the wallet
  // does not read them). What shows them, a skill's references (useReferenceContext) and the layer index
  // (useSkillLayerIndex), reads again on the world's word, so the world it is (until 2026-11-02 each one read every part
  // again: the account's limits, the verdicts and the wallet among them, three reads for nothing).
  if (path === '/api/me/skill-tools') return ['world'];
  // A DOCUMENT'S SUGGESTION TURNED DOWN moves only what waits for review: its own store answers at once
  // (lib/documentSuggestions). A file's words read (`read`) are its wrapper's parse status, which no part shows (the
  // library reads again on its own word). A removal's preview writes nothing and its `finish` is a nudge answered at
  // once (as piece-deletions/finish). Any other document write keeps, applies or takes off what the record shows, as a
  // skill's does.
  if (path === '/api/me/documents/suggestions/decline') return ['inbox'];
  if (['/api/me/documents/read', '/api/me/document-library/preview', '/api/me/document-library/finish'].includes(path)) return [];
  if (path === '/api/me/documents' || path.startsWith('/api/me/documents/') || path.startsWith('/api/me/document-library/')) return partsTouchedBy('/api/me/skills');
  // ASKING OFSELF TO SHOW MORE (lib/api/identity askToRead) changes nothing here: its consent page is the next stop
  if (path === '/api/me/identity/ask') return [];
  if (path === '/api/client-error' || path === '/api/me/skills-card-email-preview') return [];
  if (path === '/api/dev/caliber-standards' || path === '/api/dev/assessment-weight-config') return ['config', 'verdicts', 'cards', 'onboarding', 'inbox', 'world'];
  if (path.startsWith('/api/onboarding/')) return ['onboarding', 'jobSpine', 'verdicts', 'cards', 'profile', 'inbox', 'world'];
  if (path.startsWith('/api/cards')) return ['cards', 'inbox', 'world'];
  if (path.startsWith('/api/me/profile') || path.startsWith('/api/me/persona')) return ['profile', 'onboarding', 'world'];
  // a role's skills are worked out and kept nowhere (the rule below): it must be asked before /api/me/job- catches it
  if (path.startsWith('/api/me/job-skills')) return [];
  if (path.startsWith('/api/me/current-job') || path.startsWith('/api/me/job-transition') || path.startsWith('/api/me/job-')) return ['jobSpine', 'onboarding', 'verdicts', 'world'];
  if (path.startsWith('/api/skill-assessment')) return ['verdicts', 'cards', 'onboarding', 'inbox', 'world'];
  if (path.startsWith('/api/me/skills') || path.startsWith('/api/me/entities') || path.startsWith('/api/me/concept-edges')) return ['verdicts', 'onboarding', 'jobSpine', 'cards', 'inbox', 'world'];
  if (path.startsWith('/api/me/resume') || path.startsWith('/api/me/cv')) return ['verdicts', 'onboarding', 'jobSpine', 'cards', 'profile', 'inbox', 'world'];
  if (path.startsWith('/api/me/reference') || path.startsWith('/api/attest')) return ['verdicts', 'inbox', 'world'];
  // the agent may write the record; a model's answer, a search and a match read it and keep nothing. Its caliber-state
  // reach (lib/personas navigator: create and edit) holds the wallet's cards, so their count is read again too
  // (2026-11-01: the account's limits now read again only on a write that names cards)
  if (path.startsWith('/api/agent/')) return ['world', 'cards'];
  if (path.startsWith('/api/llm/') || path.startsWith('/api/me/jobs-search') || path.startsWith('/api/me/job-skills') || path.startsWith('/api/skills/match') || path.startsWith('/api/me/people/search')) return [];
  // The public page's saves, a message's status, the link reset: the page is
  // no part of the session, and the only part its writes can move is the
  // tray's (a message marked read). Before this rule they fell to 'all' and
  // forgot every part for a change none of them held.
  if (path.startsWith('/api/me/public-profile')) return ['inbox'];
  // The tray's own write (a row seen, on every device): only its part moves.
  if (path.startsWith('/api/me/inbox')) return ['inbox'];
  return ['all'];
}

/** WRITES MADE TOGETHER, READ AGAIN TWICE AT MOST (2026-10-31): a save of several writes (lib/pieceCreation: the piece,
 *  its words, a line per skill) raises WROTE_EVENT for its first write the world draws at once, naming it in `paths` as
 *  the release names its writes (wrote), so the piece it made shows as it lands (owner call 2026-10-09,
 *  e2e/server-world.browser.spec.js "slow deferred room refresh"), and holds
 *  it for the rest until its last write is in. Each write still forgets the parts it moved at once; the held event, if
 *  any of them raised it, is raised once when the last hold lets go, naming every part they moved, so what is open is
 *  read again twice a save, not once per write. A claim's word that the skills changed waits with them
 *  (skillsChanged). A hold lets go by itself after HOLD_WRITES_MS: a save that hangs never keeps the page from reading
 *  again. → its release, safe to call more than once. */
const HOLD_WRITES_MS = 20_000;
let writeHolds = 0;
let heldWrites: { paths: string[]; parts: Set<WritePart> } | null = null;
let skillsOwed = false;
let firstWriteRead = false; // the held save's first write the world draws has been read again already
export function holdWrites(): () => void {
  writeHolds++;
  let held = true;
  const release = () => {
    if (!held) return;
    held = false;
    clearTimeout(timer);
    if (--writeHolds > 0) return;
    const writes = heldWrites, owed = skillsOwed;
    heldWrites = null; skillsOwed = false; firstWriteRead = false;
    // the skills' word first: its listener here forgets the parts it names (installListeners), so a reader both events
    // ask (lib/accountLimits) reads once, the second joining the first's read
    if (owed) window.dispatchEvent(new Event('kalibrSkillsChanged'));
    if (writes && (writes.parts.has('world') || writes.parts.has('all'))) {
      window.dispatchEvent(new CustomEvent(WROTE_EVENT, { detail: { path: writes.paths[writes.paths.length - 1], paths: writes.paths, parts: [...writes.parts] } }));
    }
  };
  const timer = setTimeout(release, HOLD_WRITES_MS);
  return release;
}

/** THE SKILLS CHANGED (lib/claimSkill): said at once, or, while writes are held, once when the last hold lets go. */
export function skillsChanged(): void {
  if (writeHolds > 0) { skillsOwed = true; return; }
  window.dispatchEvent(new Event('kalibrSkillsChanged'));
}

/** A WRITE ANSWERED IN TWO PARTS (POST /api/me/record-entry making a piece with its lines, docs/PIECE_SAVE.md): its
 *  headers name the piece the moment it is made (X-Piece-Id), its body comes once its lines and its address are written.
 *  Said at its headers, a part read again then would be read without its lines, and kept so; so what it moved is said
 *  when its answer has been read, by its reader (lib/api/recordEntries makeEntry), or by its body coming in where its
 *  reader says nothing. */
const PIECE_AHEAD = 'X-Piece-Id';
const answering = new WeakMap<Response, () => void>();
/** The answer to a write has been read: what the write moved is said now, if it was not already. */
export function answered(response: Response): void { answering.get(response)?.(); }

/** THE CARD WORLD LISTENS (components/world): a write that can drop a block or land a seal says so, and the world reads
 *  the record again; while writes are held, at the first such write and once for all the rest (holdWrites). */
function wrote(path: string, parts: WritePart[]): void {
  const drawn = parts.includes('world') || parts.includes('all');
  if (writeHolds > 0 && (firstWriteRead || !drawn)) {
    if (!parts.length) return;
    heldWrites ??= { paths: [], parts: new Set() };
    heldWrites.paths.push(path);
    for (const part of parts) heldWrites.parts.add(part);
    return;
  }
  // A HELD SAVE'S LEADING WRITE IS GATHERED TOO (2026-11-01): every write of the save after it is held for the event at
  // its release, so it names its writes (`paths`) as that event does, and a view that reads a gathered event at once
  // reads it now, not a timer later. A piece made in one request (docs/PIECE_SAVE.md) is its save's one write.
  const leading = writeHolds > 0;
  if (leading) firstWriteRead = true;
  // what it moved goes with it, as the held event's does, for a reader that stands on a part it did not move
  // (lib/accountLimits: a write that moved no card leaves the account's count as read, 2026-11-01)
  if (drawn) window.dispatchEvent(new CustomEvent(WROTE_EVENT, { detail: { path, parts, ...(leading ? { paths: [path] } : {}) } }));
}

/**
 * Watch the fetch layer: any non-GET to /api/* that succeeds invalidates the
 * parts it can have changed. One place, so no write path — present or future
 * — can forget. Installed once at boot, after the dev seed (which also wraps
 * fetch), so it sees what the app sends.
 *
 * Every /api response is also read for the three things only the boundary
 * can say: a 403 GRANT_REVOKED (the approval is gone — on a GET as much as on
 * a write, since most surfaces read with a plain fetch and would otherwise
 * print the sentence in their own error block with no frame and no press),
 * a 401 SESSION_REQUIRED to a request naming the person signed in here (the
 * session ended under the page: the same sign-out sessionFetch's reads
 * raise, for the plain reads that would otherwise say "Sign in required"),
 * and a 2xx through the boundary (this page has worked). The status is
 * checked first and the body is cloned only on a 401 or a 403, so a 2xx
 * costs nothing.
 */
export function installSessionWriteWatch(): void {
  if (typeof window === 'undefined' || (window as unknown as { __kalibrSessionWatch?: boolean }).__kalibrSessionWatch) return;
  (window as unknown as { __kalibrSessionWatch?: boolean }).__kalibrSessionWatch = true;
  installListeners();
  const real = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];
    if (!path.startsWith('/api/')) return real(input, init);
    return real(input, init).then(async (r) => {
      if (r.ok) {
        if (provesStanding(path, url, init)) pageWorked = true;
        if (method !== 'GET') {
          let said = false;
          const say = () => {
            if (said) return;
            said = true;
            const parts = partsTouchedBy(path, method, init?.body);
            // `world` is a part too: the server's world, read again when the world reads again; and the atlas, Home's
            // first look, is worked out from it (lib/atlas), so it moves with it
            if (parts.length) invalidate(...parts, ...(parts.includes('world') ? (['atlas'] as const) : []));
            wrote(path, parts);
          };
          // an answer that names its piece ahead of the rest is said once it is read (answered), or once it is all in
          if (r.headers.has(PIECE_AHEAD)) { answering.set(r, say); void r.clone().arrayBuffer().then(say, say); } else say();
        }
      } else if (r.status === 403) {
        // A read or write refused for a withdrawn approval says so the same
        // way, whichever wrapper sent it.
        const revoked = await grantRevoked(r); if (revoked) grantLost(revoked.reason);
      } else if (r.status === 401 && !path.startsWith('/api/auth/') && namesUser(url, init) && !onSignInReturn() && signedIn() && await sessionRequired(r)) {
        // So does a session that ended: the gate refused a request naming the
        // person. Not the sign-in's own questions (/api/auth/*, outside the
        // gate, and the OAuth return's raw reads: the callback words their
        // answer), and not a page nobody is signed in to.
        sessionLost();
      }
      return r;
    });
  };
}
